import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SEO, { buildFaqJsonLd } from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import ContactForm from "@/components/ContactForm";
import FAQAccordion from "@/components/FAQAccordion";
import ProcessSteps from "@/components/ProcessSteps";
import CTASection from "@/components/CTASection";
import { SERVICES, ALL_SERVICE_SLUGS, slugToTitle } from "@/data/services";
import { GENERAL_FAQS } from "@/data/faqs";
import { getServiceImage } from "@/data/serviceImages";
import { Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { SITE } from "@/lib/site";

const DEFAULT_FAQS = (title) => [
  { q: `Do you provide ${title.toLowerCase()} in Austin, TX?`, a: `Yes. Black Ridge Construction provides ${title.toLowerCase()} for homeowners and property managers throughout Austin and Central Texas.` },
  { q: "How do I request a consultation?", a: `Submit the consultation form on this page or call ${SITE.phone}. We'll review your project, schedule a property visit, and provide a clear estimate.` },
  { q: "Do you handle both small projects and larger scopes?", a: "Yes. Black Ridge is a full-service general contractor. We handle small repair scopes and full-home projects alike." },
  { q: "Do you use subcontractors?", a: "For specialty trades, yes. We coordinate trusted, insured subcontractors. Maintenance work, repair scopes, and general carpentry are typically handled in-house." },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const isValidSlug = ALL_SERVICE_SLUGS.includes(slug);

  const data = isValidSlug ? SERVICES[slug] : null;
  const title = data?.short || (isValidSlug ? slugToTitle(slug) : "");

  // Hooks must run unconditionally — declare before the early return below.
  const breadcrumbs = useMemo(
    () => [{ label: "Home", to: "/" }, { label: "Services", to: "/construction-services" }, { label: title }],
    [title]
  );

  if (!isValidSlug) return <Navigate to="/construction-services" replace />;

  const fullTitle = data?.title || `${slugToTitle(slug)} in Austin, TX`;
  const metaTitle = data?.metaTitle || `${slugToTitle(slug)} Austin TX | Black Ridge Construction`;
  const metaDescription = data?.metaDescription || `Black Ridge Construction provides ${slugToTitle(slug).toLowerCase()} services for homeowners across Austin and Central Texas. One general contractor coordinating every trade involved.`;
  const hero = data?.hero || fullTitle;
  const heroImage = data?.image || getServiceImage(slug);
  const introCopy = data?.intro || `Black Ridge Construction provides ${title.toLowerCase()} for Austin and Central Texas homeowners. As a full-service general contractor, we coordinate every trade involved — not just one piece of the work. We approach each project with clear communication, fair pricing, and the kind of execution that builds long-term homeowner relationships.`;
  const overview = data?.overview || [
    `${title} is one of the residential service categories we handle regularly across the Austin area. Because we operate as a full-service general contractor, we naturally see where ${title.toLowerCase()} touches roofing, exterior, interior finishes, and ongoing maintenance — and we coordinate accordingly.`,
    `Our approach is direct and practical. We walk the property, talk through goals, give honest options at multiple price levels, and build a clear written scope so you actually know what you're paying for. Trades are sequenced to keep work moving and to avoid the kind of delays that come from disconnected single-trade installers.`,
    `Maintenance members get priority scheduling and member pricing on approved repair scopes. For homeowners outside a plan, we still aim for quick scheduling and clear communication throughout.`,
  ];
  const problemsSolved = data?.problemsSolved || [
    "Aging finishes or systems that no longer perform",
    "Coordination headaches across multiple trades",
    "Damage or wear from Texas weather, heat, and storms",
    "Layouts or installations that no longer fit how you live",
  ];
  const relatedServices = data?.relatedServices || ["home-maintenance-plans", "handyman-punch-list", "investor-property-repairs", "exterior-repairs"];
  const faqs = (data?.faqs && data.faqs.length > 0) ? data.faqs : DEFAULT_FAQS(title);

  return (
    <>
      <SEO title={metaTitle} description={metaDescription} ogImage={heroImage} jsonLd={buildFaqJsonLd(faqs)} />

      <section className="relative bg-br-black text-white">
        <div className="absolute inset-0">
          <img src={heroImage} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-br py-12 lg:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <BreadcrumbNav dark items={breadcrumbs} />
            <div className="br-eyebrow text-br-bronze-2 mt-4">Service</div>
            <h1 className="br-headline text-3xl sm:text-5xl mt-3">{hero}</h1>
            <p className="mt-4 text-gray-200 max-w-2xl leading-relaxed">{introCopy}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" className="br-btn-primary">Request a Consultation <ArrowRight size={14}/></Link>
              <a href={`tel:${SITE.phoneE164}`} className="br-btn-secondary"><Phone size={14}/> {SITE.phone}</a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-gray-900 rounded p-6 shadow-2xl">
              <ContactForm title={`Ask About ${title}`} defaultService={title} source_page={`service:${slug}`} />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-white p-6 rounded border border-gray-200 prose-br">
              <div className="br-eyebrow text-br-bronze">Service Overview</div>
              <h2 className="br-headline text-2xl mt-2">{title} in Austin & Central Texas</h2>
              <div className="mt-3 space-y-3 text-gray-800 leading-relaxed text-[15px]">
                {overview.map((p) => <p key={p.slice(0,80)}>{p}</p>)}
              </div>
            </div>

            <div className="bg-white p-6 rounded border border-gray-200">
              <div className="br-eyebrow text-br-bronze">Problems We Solve</div>
              <h2 className="br-headline text-2xl mt-2">Common {title} Issues</h2>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                {problemsSolved.map((p) => (<li key={p} className="flex gap-2 text-sm text-gray-700"><CheckCircle2 size={16} className="text-br-bronze mt-0.5 shrink-0"/> {p}</li>))}
              </ul>
            </div>

            <div className="bg-white p-6 rounded border border-gray-200">
              <div className="br-eyebrow text-br-bronze">Related Services</div>
              <h3 className="font-display text-xl mt-2 tracking-widest uppercase">Other Work Homeowners Often Pair With This</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {relatedServices.map((s) => (
                  <Link key={s} to={`/services/${s}`} className="px-3 py-2 rounded border border-gray-200 hover:border-br-bronze text-sm transition">{slugToTitle(s)}</Link>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded border border-gray-200">
              <h3 className="font-display tracking-widest uppercase text-base mb-3">Frequently Asked Questions</h3>
              <FAQAccordion faqs={faqs} />
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="bg-br-black text-white p-6 rounded lg:sticky lg:top-24">
              <div className="br-eyebrow text-br-bronze-2">Talk to a GC</div>
              <h3 className="font-display text-xl mt-2 tracking-widest uppercase">{title} Consultation</h3>
              <p className="mt-2 text-sm text-gray-300">We'll review the project, walk the property, and give honest options.</p>
              <a href={`tel:${SITE.phoneE164}`} className="mt-4 block bg-br-bronze text-white text-center font-bold py-3 rounded text-sm tracking-widest uppercase hover:bg-br-bronze-2 transition"><Phone size={14} className="inline mr-1"/> {SITE.phone}</a>
              <Link to="/contact" className="mt-2 block border border-white/30 text-center text-sm font-bold uppercase tracking-widest py-3 rounded hover:border-br-bronze hover:text-br-bronze-2 transition">Request a Consultation</Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Our Process</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Predictable Steps. Clear Communication.</h2>
          </div>
          <ProcessSteps />
        </div>
      </section>

      <CTASection />
    </>
  );
}
